import dayjs from "dayjs";
import { Config } from "@/app/config";
import { catchCalculatorError, resultListFilter } from "@/component/assets/AbstractListingCalculator/domain";
import type { ResultListResponse } from "@/component/assets/AbstractListingCalculator/types";
import type { StringPair } from "@/shared/lib/JsonDataBuilder";
import { JsonDataBuilder } from "@/shared/lib/JsonDataBuilder";
import { http } from "@/shared/ui";
import { formatText } from "@/shared/ui/format";
import type { RetailAccountCalcRequest, RetailAccountCalcResponse } from "../LeadForm/types";
import type {
	RetailAccountRequest,
	RetailAccountResponseItem,
	RetailAccountSearchParams,
	RetailAccountSort,
} from "./types";

export const INIT_VALUES: RetailAccountSearchParams = {
	receiptOfMoneyAmount: undefined,
	piecesCount: 1,
	salary: true,
	pension: false,
	userType: "worker",
	age: 30,
	bank: "none",
	averageAccountBalance: 50_000,
	averageSavings: 0,
	loanBank: "none",
	loanAmount: 0,

	internetTransferCount: 4,
	internetTransferAmount: 40_000,
	branchTransferCount: 0,
	branchTransferAmount: 0,
	phoneTransferCount: 0,
	phoneTransferAmount: 0,
	fixedTransferCount: 0,
	fixedTransferAmount: 0,
	directDebitCount: 2,
	directDebitAmount: 15_000,

	cashWithdrawalOwnAtmCount: 2,
	cashWithdrawalOwnAtmAmount: 90_000,
	cashWithdrawalForeignAtmCount: 0,
	cashWithdrawalForeignAtmAmount: 0,
	useFreeCashWithdrawal: true,
	cardPurchaseCount: 30,
	cardPurchaseAmount: 75_000,

	smsServiceTransfers: false,
	smsServiceCardUsage: false,
	smsServiceNetbankLogin: false,
	postalAccountBalance: false,

	creditCardCategory: "NO_PREFERED",
	calculationDate: dayjs().format("YYYY-MM-DD"),

	onePerBank: true,
};

export function searchParamsToRequest(
	searchParams: Partial<RetailAccountSearchParams>,
	sort: RetailAccountSort = undefined,
): RetailAccountRequest {
	const { userType, loanBank, loanAmount, bank, onePerBank, ...other } = {
		...INIT_VALUES,
		...searchParams,
	};
	const request: RetailAccountRequest = {
		student: userType === "student",
		pensioner: userType === "pensioner",
		publicServant: userType === "publicServant",
		loanBank: loanBank === "none" ? undefined : loanBank,
		loanAmount: loanBank === "none" ? 0 : loanAmount,
		creditCardCategory: "NO_PREFERED",
		bank: bank === "none" ? undefined : bank,
		// CalculationDate: dayjs().format('YYYY-MM-DD'),
		roleCode: "BM",
		comparator: sort,
		userType, // TODO: Átmeneti
		onePerBank,
		...other,
	};

	return request;
}

export async function calculate(
	request: RetailAccountRequest,
	onePerBank = true,
): Promise<ResultListResponse<RetailAccountResponseItem>> {
	return http
		.get(
			Config.getApiUrl(
				onePerBank ? "/api/public/account-packages/search/one-per-bank" : "/api/public/account-packages/search",
				request,
			),
		)
		.then((result: ResultListResponse<RetailAccountResponseItem>) => {
			if (!result?.normalList) {
				throw new Error("Nincs a keresésnek megfelelő eredmény.");
			}

			return result;
		})
		.catch(catchCalculatorError);
}

export function responseToResult(response: any): ResultListResponse<RetailAccountResponseItem> {
	return resultListFilter<RetailAccountResponseItem>(response);
}

export async function exportXls(searchParams: RetailAccountSearchParams, sort: RetailAccountSort): Promise<string> {
	const request: RetailAccountRequest = searchParamsToRequest(searchParams, sort);

	return http.get(Config.getApiUrl("/api/private/bank-accounts/export", request));
}

export function getLeadCalculatorRequest(_searchParams: RetailAccountSearchParams): RetailAccountCalcRequest {
	return {};
}

export function getLeadCalculatorResponse(response: RetailAccountResponseItem): RetailAccountCalcResponse {
	return {
		id: response?.id,
		headId: response?.headId,
		productName: response?.name,
		bankCode: response?.bank?.code,
		bankName: response?.bank?.name,
	};
}

export class BankAccountsInfo {
	public static getSearchParametersText(request: RetailAccountRequest): StringPair[] {
		const data = new JsonDataBuilder();
		data.addTextItem(
			"Csoportos beszedés",
			`${formatText(request.directDebitAmount, "ft")} (${formatText(request.directDebitCount, "db")})`,
		);
		data.addTextItem(
			"Állandó átutalás",
			`${formatText(request.fixedTransferAmount, "ft")} (${formatText(request.fixedTransferCount, "db")})`,
		);
		data.addTextItem(
			"Netbanki utalás",
			`${formatText(request.internetTransferAmount, "ft")} (${formatText(request.internetTransferCount, "db")})`,
		);
		data.addTextItem(
			"ATM (saját)",
			`${formatText(request.cashWithdrawalOwnAtmAmount, "ft")} (${formatText(
				request.cashWithdrawalOwnAtmCount,
				"db",
			)})`,
		);
		data.addTextItem(
			"ATM (idegen)",
			`${formatText(request.cashWithdrawalForeignAtmAmount, "ft")} (${formatText(
				request.cashWithdrawalForeignAtmCount,
				"db",
			)})`,
		);
		data.addTextItem("SMS értesítés", request.smsServiceTransfers ? "igen" : "nem");
		data.addTextItem("Számlakivonat", request.postalAccountBalance ? "igen" : "nem");
		data.addTextItem(
			"Számlára érkező összeg",
			`${formatText(request.receiptOfMoneyAmount, "ft")} (${formatText(request.piecesCount, "db")})`,
		);

		return data.getDataArray();
	}

	public static getProductParametersText(
		_request: RetailAccountRequest,
		response: RetailAccountResponseItem,
	): StringPair[] {
		const data = new JsonDataBuilder();
		data.addTextItem("Össz. havi költség", response.firstYear.monthlyTotalFee, "ft");
		data.addTextItem("Alap díjak", response.firstYear.monthlyCost, "ft");
		data.addTextItem("Tranzakciók", response.firstYear.totalTransferCost, "ft");
		data.addTextItem("Kártyahasználat", response.firstYear.cardUsageCost, "ft");
		data.addTextItem("Egyéb", response.firstYear.otherCosts, "ft");

		return data.getDataArray();
	}
}

export const BASIC_SEARCH_FIELDS = [
	"receiptOfMoneyAmount",
	"piecesCount",
	"salary",
	"pension",
	"age",
	"userType",
	"userType",
];
